import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const money = (n) => (Math.abs(n) >= 1e6 ? `$${(n / 1e6).toFixed(2)}m` : `$${Math.round(n || 0).toLocaleString()}`);

const format = (value, kind) => {
  if (value === null || value === undefined) return "—";
  if (kind === "money") return money(value);
  if (kind === "pct") return `${value}%`;
  if (kind === "count") return Math.round(value).toLocaleString();
  return `${value}`;
};

const Picker = ({ label, value, onChange, options, testId }) => (
  <label className="flex flex-col gap-2">
    <span className="overline !text-zinc-500">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="border border-white/20 bg-black px-4 py-3 font-display text-lg uppercase tracking-tight text-white focus:border-ochre focus:outline-none"
    >
      {options.map((r) => (
        <option key={r.county} value={r.county}>
          {`${r.county} — #${r.rank}`}
        </option>
      ))}
    </select>
  </label>
);

export default function CountyCompareTwo() {
  const [all, setAll] = useState([]);
  const [a, setA] = useState("Nairobi");
  const [b, setB] = useState("Kisumu");
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useSeo({
    title: "County Compare",
    description:
      "Put two Kenyan county dossiers side by side and see which dollar works harder: build cost, jobs, players, professionals produced, trading income, health value and value per dollar built.",
  });

  useEffect(() => {
    api.get("/priority-counties").then((r) => setAll(r.data.rows)).catch(() => setErr("We could not load the counties."));
  }, []);

  useEffect(() => {
    if (!a || !b || a === b) return;
    api.get(`/county-compare?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`)
      .then((r) => setD(r.data))
      .catch(() => setErr("We could not compare those counties."));
  }, [a, b]);

  return (
    <div className="bg-black pb-24 pt-10 md:pt-14" data-testid="compare-page">
      <section className="px-5 md:px-10">
        <div className="mx-auto max-w-[1500px]">
          <Reveal>
            <p className="eyebrow">County compare</p>
            <h1 className="display-1 mt-5 max-w-4xl">Which County Works Harder</h1>
            <p className="body-copy mt-6 max-w-3xl">
              Two counties, the same twenty-one measures, no averages. Cheaper wins on the cost lines, higher wins
              everywhere else, and the verdict is decided on how much annual social and economic value each dollar of
              build cost returns.
            </p>
          </Reveal>

          {err ? (
            <p className="mt-8 border border-maasai/40 bg-maasai/10 p-6 text-sm text-white" data-testid="compare-error">{err}</p>
          ) : null}

          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:max-w-3xl" data-testid="compare-pickers">
            <Picker label="County A" value={a} onChange={setA} options={all} testId="compare-select-a" />
            <Picker label="County B" value={b} onChange={setB} options={all} testId="compare-select-b" />
          </div>
          {a === b ? (
            <p className="mt-4 text-sm text-ochre" data-testid="compare-same">Choose two different counties.</p>
          ) : null}

          {d && a !== b ? (
            <>
              <div className="mt-12 grid gap-px border border-zinc-800 bg-zinc-800 md:grid-cols-2" data-testid="compare-verdict">
                {[d.left, d.right].map((side) => {
                  const winner = d.verdict.county === side.county;
                  return (
                    <div key={side.county} className={`bg-[#0b0b0b] p-7 ${winner ? "border-l-2 border-maasai" : ""}`} data-testid={`compare-side-${slug(side.county)}`}>
                      <p className="mono text-[0.62rem] uppercase tracking-[0.16em] text-zinc-500">
                        #{side.priority_rank} of 47 {side.priority ? "· Priority 18" : ""}
                      </p>
                      <h2 className="display-3 mt-3">{side.county}</h2>
                      <p className="mono mt-4 text-2xl font-bold text-ochre md:text-3xl">${side.value_per_dollar}</p>
                      <p className="mt-2 text-[0.66rem] uppercase tracking-[0.14em] text-zinc-400">
                        Annual value per dollar built · {d.wins[side.county]} of 21 measures won
                      </p>
                      {winner ? (
                        <p className="mono mt-4 inline-block bg-maasai px-2 py-1 text-[0.55rem] uppercase tracking-[0.16em] text-white">
                          Harder-working dollar
                        </p>
                      ) : null}
                      <Link
                        to={`/counties/${slug(side.county)}`}
                        className="btn-ghost mt-6"
                        data-testid={`compare-dossier-${slug(side.county)}`}
                      >
                        Full dossier <ArrowUpRight size={14} />
                      </Link>
                    </div>
                  );
                })}
              </div>
              <p className="mt-4 max-w-4xl text-xs leading-relaxed text-zinc-400" data-testid="compare-note">{d.verdict.note}</p>

              <div className="mt-12 overflow-x-auto border border-zinc-800" data-testid="compare-table">
                <table className="w-full min-w-[780px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                      {["Measure", d.left.county, d.right.county, "Gap", "Better"].map((h) => (
                        <th key={h} className="px-4 py-3 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-zinc-300">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {d.metrics.map((mt, i) => (
                      <tr key={mt.key} className="border-b border-zinc-900" data-testid={`compare-row-${mt.key}`}>
                        <td className="px-4 py-3 text-sm text-white">
                          {mt.label}
                          {mt.lower_is_better ? (
                            <span className="mono ml-2 text-[0.5rem] uppercase tracking-[0.14em] text-zinc-400">lower is better</span>
                          ) : null}
                        </td>
                        <td className={`mono px-4 py-3 text-sm ${mt.winner === d.left.county ? "text-ochre" : "text-zinc-400"}`}>
                          {format(mt.left, mt.kind)}
                        </td>
                        <td className={`mono px-4 py-3 text-sm ${mt.winner === d.right.county ? "text-ochre" : "text-zinc-400"}`}>
                          {format(mt.right, mt.kind)}
                        </td>
                        <td className="mono px-4 py-3 text-sm text-zinc-500">{mt.gap_pct}%</td>
                        <td className="px-4 py-3 text-[0.6rem] uppercase tracking-[0.14em] text-maasai">
                          {mt.winner || "Level"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/county-scenarios" className="btn-primary" data-testid="compare-shortlist-link">
                  Build county funding scenarios <ArrowUpRight size={14} />
                </Link>
                <Link to={`/counties/compare-sheet?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`}
                  className="btn-ghost" data-testid="compare-sheet-link">
                  Printable one-page sheet <ArrowUpRight size={14} />
                </Link>
                <Link to="/counties" className="btn-ghost" data-testid="compare-map-link">
                  County map
                </Link>
              </div>
            </>
          ) : null}
        </div>
      </section>
    </div>
  );
}
